import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FolderView, ItemView, View, Views } from '../../types/Spreadsheet';
import { RootState } from '../store';
import { uuid } from '@utils/uuid';

interface ViewsState {
  views: Views;
  activeViewId?: string;
}

const initialState: ViewsState = {
  views: [],
  activeViewId: undefined
};
const traverseTree = ( views: Views, callback: ( currentView: View, index: number, siblingsRef: View[], stopTraversal: () => void ) => void ): void => {
  let shouldStop = false;
  const stopTraversal = () => {
    shouldStop = true;
  };

  function _recursive( list: View[]): void {
    for ( let i = 0; i < list.length; i++ ) {
      if ( shouldStop ) return;
      const view = list[i];
      callback( view, i, list, stopTraversal );
      if ( shouldStop ) return;
      if ( view.type === 'folder' ) {
        _recursive(( view as FolderView ).children );
        if ( shouldStop ) return;
      }
    }
  }
  _recursive( views );
};

function addChild( views: Views, folderId: string, newView: View ): void {
  traverseTree( views, ( currentView, _index, _siblingsRef, stopTraversal ) => {
    if ( currentView.type === 'folder' && currentView.id === folderId ) {
      ( currentView as FolderView ).children.push( newView );
      stopTraversal();
    }
  });
}

function removeById( views: Views, targetId: string ): void {
  traverseTree( views, ( currentView, index, siblingsRef, stopTraversal ) => {
    if ( currentView.id === targetId ) {
      siblingsRef.splice( index, 1 );
      stopTraversal();
    }
  });
}

function findById( views: Views, targetId: string ): View | undefined {
  let foundView: View | undefined;
  traverseTree( views, ( currentView, _index, _siblingsRef, stopTraversal ) => {
    if ( currentView.id === targetId ) {
      foundView = currentView;
      stopTraversal();
    }
  });
  return foundView;
}


function updateById( views: Views, targetId: string, changes: Partial<View> ): void {
  traverseTree( views, ( currentView, _index, _siblingsRef, stopTraversal ) => {
    if ( currentView.id === targetId ) {
      Object.assign( currentView, changes );
      stopTraversal();
    }
  });
}

function duplicateById( views: Views, targetId: string ): void {
  traverseTree( views, ( currentView, index, siblingsRef, stopTraversal ) => {
    if ( currentView.id === targetId ) {
      const copy: View = {
        ...currentView,
        id: uuid(),
        label: `${currentView.label } copy`
      };
      siblingsRef.splice( index + 1, 0, copy );
      stopTraversal();
    }
  });
}

const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    addRootView( state, action: PayloadAction<View> ) {
      state.views.push( action.payload );
    },
    addChildView( state, action: PayloadAction<{ folderId: string; view: View }> ) {
      const { folderId, view } = action.payload;
      addChild( state.views, folderId, view );
    },
    removeView( state, action: PayloadAction<string> ) {
      removeById( state.views, action.payload );
    },
    setViews( state, action: PayloadAction<Views> ) {
      state.views = action.payload;
    },
    clearViews( state ) {
      state.views = [];
    },
    setActiveView( state, action: PayloadAction<string | undefined> ) {
      state.activeViewId = action.payload;
    },
    clearActiveView( state ) {
      state.activeViewId = undefined;
    },

    updateView( state, action: PayloadAction<{ id: string; changes: Partial<View> }> ) {
      const { id, changes } = action.payload;
      updateById( state.views, id, changes );
    },
    duplicateView( state, action: PayloadAction<string> ) {
      duplicateById( state.views, action.payload );
    },
    moveView( state, action: PayloadAction<{ viewId: string; folderId: string }> ) {
      const { viewId, folderId } = action.payload;
      const viewToMove = findById( state.views, viewId );
      if ( viewToMove ) {
        removeById( state.views, viewId );
        if ( folderId === 'root' ) {
          state.views.push( viewToMove );
        } else {
          addChild( state.views, folderId, viewToMove );
        }
      }
    }
  }
});

export const {
  addRootView,
  addChildView,
  removeView,
  setViews,
  clearViews,
  setActiveView,
  clearActiveView,
  updateView,
  duplicateView,
  moveView
} = viewsSlice.actions;

export default viewsSlice.reducer;

export const selectViews = ( state: RootState ): Views => state.spreadsheet.views.views;
export const selectActiveView = ( state: RootState ): ItemView | undefined => {
  const { activeViewId, views } = state.spreadsheet.views;
  if ( !activeViewId ) return undefined;
  const found = findById( views, activeViewId );
  return found && found.type === 'view' ? ( found as ItemView ) : undefined;
};